/**
 * TOWER DESIGNER + EQUIPMENT LOADOUT PANEL.
 *
 * A self-contained DOM panel that lets a user design a `TowerSpec` live and load
 * it with equipment from the appurtenance `CATALOG`. It owns its own controls
 * (built into the `container` passed to the constructor), and on ANY edit it
 * rebuilds the spec, validates it (inline errors/warnings, warnings non-blocking)
 * and emits the fresh spec through `opts.onChange` so the host app can rebuild +
 * re-simulate the tower.
 *
 * It does NOT compile, render or simulate anything itself — it is purely an
 * editor that produces a `TowerSpec`. The host wires it like:
 *
 *   new TowerDesigner(el, { initial: spec, onChange: (s) => rebuildFromSpec(s) });
 *
 * The emitted spec is the exact `TowerSpec` shape from `@/game/towerSpec`:
 *   { type, height, stack:{faces,panels,baseRadius,topRadius,legSection,
 *     braceSection,legMaterial?,braceMaterial?,bracing}, guys?:GuyRing[],
 *     shell?:{baseDiameter,topDiameter}, loads?:MountedLoad[] }
 * — section/material fields are catalog KEYS (SectionKey / MaterialKey strings),
 * and each load's `appurtenance` is a full CATALOG object (not a key).
 */
import {
  SECTIONS,
  MATERIALS,
  MATERIAL_CATEGORY,
  MATERIAL_CATEGORY_ORDER,
  type SectionKey,
  type MaterialKey,
  type MaterialCategory,
} from "@/physics/model";
import {
  type TowerSpec,
  type TowerType,
  type GuyRing,
  type PanelStack,
  type TubeShell,
  type MemberTypeSpec,
  type SectionRef,
  type MaterialRef,
  validateTower,
} from "@/game/towerSpec";
import { CATALOG, type CatalogKey, type MountedLoad } from "@/game/appurtenances";
import {
  SOIL_PRESETS,
  CONCRETE_GRADES,
  type SoilType,
  type ConcreteGradeKey,
  type FoundationType,
} from "@/physics/foundation";

export interface TowerDesignerOptions {
  initial: TowerSpec;
  onChange: (spec: TowerSpec) => void;
}

const SECTION_KEYS = Object.keys(SECTIONS) as SectionKey[];
const MATERIAL_KEYS = Object.keys(MATERIALS) as MaterialKey[];
const CATALOG_KEYS = Object.keys(CATALOG) as CatalogKey[];

/** Fallback stack when a spec has none (e.g. a multi-`segments` tower loaded into
 * the stack-based designer — segments aren't editable here, so we start fresh). */
const DEFAULT_STACK: PanelStack = {
  faces: 4,
  panels: 12,
  baseRadius: 2.0,
  topRadius: 0.8,
  legSection: "legHeavy",
  braceSection: "braceMed",
  bracing: "X",
};

/** Deep-ish clone of a spec so external mutation can't leak into our state. */
function cloneSpec(spec: TowerSpec): TowerSpec {
  return {
    type: spec.type,
    height: spec.height,
    legTypes: spec.legTypes?.map((t) => ({ ...t })),
    braceTypes: spec.braceTypes?.map((t) => ({ ...t })),
    guyTypes: spec.guyTypes?.map((t) => ({ ...t })),
    stack: spec.stack ? { ...spec.stack } : { ...DEFAULT_STACK },
    segments: spec.segments ? { ...spec.segments, sections: spec.segments.sections.map((s) => ({ ...s })) } : undefined,
    mesh: spec.mesh ? { ...spec.mesh, nodes: spec.mesh.nodes.map((n) => [...n] as [number, number, number]), edges: spec.mesh.edges.map((e) => [...e] as [number, number]) } : undefined,
    crossarms: spec.crossarms?.map((a) => ({ ...a })),
    masts: spec.masts?.map((m) => ({ ...m })),
    arches: spec.arches?.map((a) => ({ ...a })),
    guys: spec.guys?.map((g) => ({ ...g })),
    shell: spec.shell ? { ...spec.shell } : undefined,
    foundation: spec.foundation,
    // MountedLoad keeps a reference to the shared CATALOG object (immutable); we
    // clone the mount envelope so height/azimuth edits don't alias.
    loads: spec.loads?.map((l) => ({ ...l })),
  };
}

/** Resolve a SectionRef down to a display string (key, or inline name). */
function sectionLabel(ref: SectionRef): string {
  return typeof ref === "string" ? ref : ref.name;
}
/**
 * Resolve a spec material ref (MaterialKey | inline Material | undefined) down to
 * a catalog MaterialKey for the grouped picker. Defaults to a572; an inline
 * Material is matched to a catalog key by name (falling back to a572 if unknown).
 */
function resolveMaterialKey(ref: MaterialRef | undefined): MaterialKey {
  if (ref == null) return "a572";
  if (typeof ref === "string") {
    return (ref in MATERIALS ? ref : "a572") as MaterialKey;
  }
  const hit = MATERIAL_KEYS.find((k) => MATERIALS[k].name === ref.name);
  return hit ?? "a572";
}

/** Concise option text for a material: name + its key tradeoff numbers. */
function materialOptionText(k: MaterialKey): string {
  const m = MATERIALS[k];
  const Fy = Math.round(m.Fy / 1e6);
  const E = Math.round(m.E / 1e9);
  const rho = Math.round(m.rho);
  return `${m.name} — Fy ${Fy} MPa · E ${E} GPa · ρ ${rho}`;
}

/** Richer HTML preview of the selected material's stiffness/strength/weight. */
function materialPreviewHtml(k: MaterialKey): string {
  const m = MATERIALS[k];
  const Fy = Math.round(m.Fy / 1e6);
  const E = Math.round(m.E / 1e9);
  const rho = Math.round(m.rho);
  // strength-to-weight as a quick, honest "how good for a tower" read.
  const specific = Math.round(m.Fy / m.rho / 1e3); // (MPa)/(g/cm³) ≈ kN·m/kg proxy
  const cat = MATERIAL_CATEGORY[k];
  const novelty =
    cat === "Novelty"
      ? ` <span style="color:#f0883e;">⚠ novelty — expect a spectacular collapse</span>`
      : "";
  return (
    `<b style="color:#9fb0c3;">${cat}</b> · yield <b>${Fy}</b> MPa · ` +
    `stiffness E <b>${E}</b> GPa · density <b>${rho}</b> kg/m³ · ` +
    `strength/weight <b>${specific}</b>${novelty}`
  );
}

export class TowerDesigner {
  private readonly container: HTMLElement;
  private readonly onChange: (spec: TowerSpec) => void;
  private spec: TowerSpec;

  // dynamic regions we re-render in place
  private root!: HTMLDivElement;
  private guysList!: HTMLDivElement;
  private loadsList!: HTMLDivElement;
  private validationBox!: HTMLDivElement;
  private collapsedGroups = new Set<string>([
    "Tube Shell",
    "Guy Rings",
    "Foundation",
    "Equipment",
  ]);
  // When a hand-drawn tower is active, the parametric geometry/material controls
  // don't affect it — we hide them so the panel is about the drawing, not a
  // parallel spec the user can't see the effect of.
  private drawnMode = false;

  constructor(container: HTMLElement, opts: TowerDesignerOptions) {
    this.container = container;
    this.onChange = opts.onChange;
    this.spec = cloneSpec(opts.initial);
    this.build();
    // Emit + validate once so the host is in sync with our initial state.
    this.emit();
  }

  /** Replace the whole spec (host-driven), re-render controls, do NOT emit. */
  setSpec(spec: TowerSpec): void {
    this.spec = cloneSpec(spec);
    this.renderAll();
  }

  /**
   * Tell the panel whether the active tower is hand-drawn (shape from the free
   * draw) or parametric (shape from these controls). In drawn mode the parametric
   * geometry/material sections are hidden — they'd silently do nothing.
   */
  setDrawnMode(drawn: boolean): void {
    if (this.drawnMode === drawn) return;
    this.drawnMode = drawn;
    this.renderAll();
  }

  /** Current spec (a clone — safe to mutate by the caller). */
  getSpec(): TowerSpec {
    return cloneSpec(this.spec);
  }

  dispose(): void {
    this.root?.remove();
  }

  // ---- emit + validation -------------------------------------------------

  private emit(): void {
    this.runValidation();
    this.onChange(this.getSpec());
  }

  private runValidation(): void {
    const res = validateTower({
      id: "designer-preview",
      name: "Designer Preview",
      description: "live design from the tower designer",
      spec: this.spec,
    });
    const box = this.validationBox;
    box.innerHTML = "";
    if (res.errors.length === 0 && res.warnings.length === 0) {
      const ok = document.createElement("div");
      ok.textContent = "✓ Valid — no issues";
      ok.style.cssText = "color:#4ade80;font-size:12px;";
      box.appendChild(ok);
      return;
    }
    for (const e of res.errors) box.appendChild(this.issueRow(e, "error"));
    for (const w of res.warnings) box.appendChild(this.issueRow(w, "warn"));
  }

  private issueRow(text: string, kind: "error" | "warn"): HTMLDivElement {
    const row = document.createElement("div");
    const color = kind === "error" ? "#f87171" : "#fbbf24";
    const badge = kind === "error" ? "ERROR" : "WARN";
    row.style.cssText = `color:${color};font-size:12px;line-height:1.4;margin:2px 0;`;
    const b = document.createElement("span");
    b.textContent = badge;
    b.style.cssText = `display:inline-block;min-width:44px;font-weight:700;font-size:10px;letter-spacing:.5px;margin-right:6px;`;
    row.appendChild(b);
    row.appendChild(document.createTextNode(text));
    return row;
  }

  // ---- styling helpers ---------------------------------------------------

  private renderModeBanner(): HTMLDivElement {
    const drawn = this.drawnMode;
    const b = document.createElement("div");
    b.style.cssText =
      `border:1px solid ${drawn ? "#1d5c86" : "#2a4a38"};background:${drawn ? "#0e2233" : "#10251d"};` +
      "border-radius:8px;padding:8px 10px;margin:0 0 10px;font-size:11px;line-height:1.45;color:#b9c8d6;";
    const title = document.createElement("div");
    title.style.cssText = `font-weight:700;margin:0 0 2px;color:${drawn ? "#7dd3fc" : "#7be0b0"};`;
    title.textContent = drawn ? "✏ Hand-drawn tower" : "⚙ Parametric tower";
    const body = document.createElement("div");
    body.textContent = drawn
      ? "Shape comes from your drawing below. These controls set the ground and equipment."
      : "Set the shape with the controls below — or switch to Draw to build it by hand.";
    b.append(title, body);
    return b;
  }

  private groupEl(title: string): HTMLDetailsElement {
    const g = document.createElement("details");
    g.open = !this.collapsedGroups.has(title);
    g.style.cssText =
      "border:1px solid #1c2530;border-radius:8px;padding:10px 12px;margin:0 0 10px;background:#0d1219;";
    const h = document.createElement("summary");
    h.textContent = title;
    h.style.cssText =
      "font-size:11px;font-weight:700;letter-spacing:.8px;text-transform:uppercase;color:#7dd3fc;margin:0 0 8px;cursor:pointer;";
    g.addEventListener("toggle", () => {
      if (g.open) this.collapsedGroups.delete(title);
      else this.collapsedGroups.add(title);
    });
    g.appendChild(h);
    return g;
  }

  private labelEl(text: string): HTMLLabelElement {
    const l = document.createElement("label");
    l.textContent = text;
    l.style.cssText =
      "display:block;font-size:11px;color:#9fb0c3;margin:0 0 3px;";
    return l;
  }

  private styleInput(el: HTMLElement): void {
    el.style.cssText =
      "width:100%;box-sizing:border-box;background:#111823;color:#e6edf3;" +
      "border:1px solid #263243;border-radius:6px;padding:5px 7px;font-size:12px;outline:none;";
  }

  private button(text: string, variant: "primary" | "danger" | "ghost" = "primary"): HTMLButtonElement {
    const b = document.createElement("button");
    b.type = "button";
    b.textContent = text;
    const bg =
      variant === "danger" ? "#3a1518" : variant === "ghost" ? "#141c27" : "#123047";
    const bc =
      variant === "danger" ? "#7f2a30" : variant === "ghost" ? "#263243" : "#1d5c86";
    const fg = variant === "danger" ? "#fca5a5" : "#cfe8ff";
    b.style.cssText =
      `background:${bg};color:${fg};border:1px solid ${bc};border-radius:6px;` +
      "padding:5px 10px;font-size:12px;cursor:pointer;";
    return b;
  }

  /** A number field with a live value label; calls back on input. */
  private numberField(
    label: string,
    value: number,
    min: number,
    max: number,
    step: number,
    onInput: (v: number) => void,
    unit = "",
  ): HTMLDivElement {
    const wrap = document.createElement("div");
    wrap.style.cssText = "margin:0 0 8px;";
    const lab = this.labelEl(label);
    const valSpan = document.createElement("span");
    valSpan.style.cssText = "float:right;color:#e6edf3;font-variant-numeric:tabular-nums;";
    const fmt = (v: number) => `${Number.isInteger(step) ? v : v.toFixed(step < 0.01 ? 4 : step < 1 ? 3 : 0)}${unit ? " " + unit : ""}`;
    valSpan.textContent = fmt(value);
    lab.appendChild(valSpan);
    wrap.appendChild(lab);

    const row = document.createElement("div");
    row.style.cssText = "display:flex;gap:6px;align-items:center;";
    const range = document.createElement("input");
    range.type = "range";
    range.min = String(min);
    range.max = String(max);
    range.step = String(step);
    range.value = String(value);
    range.style.cssText = "flex:1;accent-color:#38bdf8;";
    const num = document.createElement("input");
    num.type = "number";
    num.min = String(min);
    num.max = String(max);
    num.step = String(step);
    num.value = String(value);
    num.style.cssText =
      "width:76px;box-sizing:border-box;background:#111823;color:#e6edf3;" +
      "border:1px solid #263243;border-radius:6px;padding:4px 6px;font-size:12px;";

    const clamp = (v: number) => Math.min(max, Math.max(min, v));

    // Live update WHILE typing: reflect the value into the slider + model, but do
    // NOT rewrite the text box — overwriting/ clamping it on every keystroke fights
    // the caret and makes it impossible to type (e.g. typing "40" clamps "4" to the
    // min first). An empty/partial field is simply ignored until it parses.
    const live = (raw: string) => {
      if (raw.trim() === "") return;
      const v = Number(raw);
      if (!Number.isFinite(v)) return;
      const c = clamp(v);
      range.value = String(c);
      valSpan.textContent = fmt(c);
      onInput(c);
    };
    // Commit on blur / Enter: normalise the text box to the clamped value.
    const commit = () => {
      let v = Number(num.value);
      if (!Number.isFinite(v) || num.value.trim() === "") v = Number(range.value);
      v = clamp(v);
      num.value = String(v);
      range.value = String(v);
      valSpan.textContent = fmt(v);
      onInput(v);
    };
    range.addEventListener("input", () => {
      num.value = String(range.value); // slider drives the text box (not being typed into)
      live(range.value);
    });
    num.addEventListener("input", () => live(num.value));
    num.addEventListener("change", commit);
    num.addEventListener("blur", commit);

    row.appendChild(range);
    row.appendChild(num);
    wrap.appendChild(row);
    return wrap;
  }

  private selectField(
    label: string,
    options: Array<{ value: string; text: string }>,
    selected: string,
    onChange: (v: string) => void,
  ): HTMLDivElement {
    const wrap = document.createElement("div");
    wrap.style.cssText = "margin:0 0 8px;";
    wrap.appendChild(this.labelEl(label));
    const sel = document.createElement("select");
    this.styleInput(sel);
    for (const o of options) {
      const opt = document.createElement("option");
      opt.value = o.value;
      opt.textContent = o.text;
      if (o.value === selected) opt.selected = true;
      sel.appendChild(opt);
    }
    sel.addEventListener("change", () => onChange(sel.value));
    wrap.appendChild(sel);
    return wrap;
  }

  private sectionOptions(): Array<{ value: string; text: string }> {
    return SECTION_KEYS.map((k) => ({ value: k, text: `${k} — ${SECTIONS[k].name}` }));
  }

  private ensureMemberTypes(): void {
    const st = (this.spec.stack ??= { ...DEFAULT_STACK });
    if (!this.spec.legTypes?.length) {
      this.spec.legTypes = [{ id: "leg-1", name: "Leg 1", section: st.legSection, material: st.legMaterial ?? "a572" }];
      st.legType = "leg-1";
    }
    if (!this.spec.braceTypes?.length) {
      this.spec.braceTypes = [{ id: "brace-1", name: "Brace 1", section: st.braceSection, material: st.braceMaterial ?? "a572" }];
      st.braceType = "brace-1";
    }
    if (!this.spec.guyTypes?.length) {
      this.spec.guyTypes = [{ id: "guy-1", name: "Guy 1", section: "guy38", material: "guyStrand" }];
    }
  }

  private typeOptions(types: MemberTypeSpec[]): Array<{ value: string; text: string }> {
    return types.map((t) => ({ value: t.id, text: `${t.name || t.id} — ${sectionLabel(t.section)}` }));
  }

  private syncStackDefaults(): void {
    const st = this.spec.stack;
    if (!st) return;
    const leg = this.spec.legTypes?.find((t) => t.id === st.legType);
    if (leg) {
      st.legSection = leg.section;
      st.legMaterial = leg.material;
    }
    const brace = this.spec.braceTypes?.find((t) => t.id === st.braceType);
    if (brace) {
      st.braceSection = brace.section;
      st.braceMaterial = brace.material;
    }
  }

  private renderMemberTypeEditor(label: string, types: MemberTypeSpec[], addPrefix: string, afterChange: () => void): HTMLElement {
    const wrap = document.createElement("div");
    wrap.style.cssText = "border-top:1px solid #1c2530;margin:10px 0 0;padding:10px 0 0;";
    const head = document.createElement("div");
    head.style.cssText = "display:flex;justify-content:space-between;align-items:center;margin:0 0 8px;";
    const title = document.createElement("span");
    title.textContent = label;
    title.style.cssText = "font-size:12px;font-weight:700;color:#cfe8ff;";
    const add = this.button("+ Add", "ghost");
    add.addEventListener("click", () => {
      const n = types.length + 1;
      const fallbackSection = addPrefix === "guy" ? "guy38" : addPrefix === "leg" ? "legHeavy" : "braceMed";
      const fallbackMaterial = addPrefix === "guy" ? "guyStrand" : "a572";
      types.push({
        id: `${addPrefix}-${n}`,
        name: `${label.slice(0, -1)} ${n}`,
        section: types[0]?.section ?? fallbackSection,
        material: types[0]?.material ?? fallbackMaterial,
      });
      afterChange();
      this.renderAll();
      this.emit();
    });
    head.append(title, add);
    wrap.appendChild(head);

    types.forEach((t, i) => {
      const card = document.createElement("div");
      card.style.cssText = "border:1px solid #223142;border-radius:6px;padding:8px;margin:0 0 8px;background:#0f1620;";
      const name = document.createElement("input");
      name.value = t.name ?? t.id;
      this.styleInput(name);
      name.addEventListener("input", () => {
        t.name = name.value || t.id;
        afterChange();
        this.emit();
      });
      card.appendChild(this.labelEl("Name"));
      card.appendChild(name);
      card.appendChild(
        this.selectField("Section", this.sectionOptions(), sectionLabel(t.section), (v) => {
          t.section = v as SectionKey;
          afterChange();
          this.emit();
        }),
      );
      card.appendChild(
        this.materialSelectField("Material", resolveMaterialKey(t.material), (v) => {
          t.material = v;
          afterChange();
          this.emit();
        }),
      );
      if (types.length > 1) {
        const rm = this.button("Remove", "danger");
        rm.addEventListener("click", () => {
          types.splice(i, 1);
          afterChange();
          this.renderAll();
          this.emit();
        });
        card.appendChild(rm);
      }
      wrap.appendChild(card);
    });
    return wrap;
  }

  /**
   * A material picker grouped into <optgroup>s by category (Structural steel /
   * Metals / Composites / Natural / Novelty), with each option surfacing the key
   * tradeoff numbers (yield, stiffness E, density). A live preview line under the
   * select restates the selected material's numbers so the player understands the
   * tradeoff, not just a name.
   */
  private materialSelectField(
    label: string,
    selected: MaterialKey,
    onChange: (v: MaterialKey) => void,
  ): HTMLDivElement {
    const wrap = document.createElement("div");
    wrap.style.cssText = "margin:0 0 8px;";
    wrap.appendChild(this.labelEl(label));

    const sel = document.createElement("select");
    this.styleInput(sel);

    // Bucket keys by category, preserving catalog order within each.
    const byCat = new Map<MaterialCategory, MaterialKey[]>();
    for (const k of MATERIAL_KEYS) {
      const cat = MATERIAL_CATEGORY[k];
      (byCat.get(cat) ?? byCat.set(cat, []).get(cat)!).push(k);
    }
    for (const cat of MATERIAL_CATEGORY_ORDER) {
      const keys = byCat.get(cat);
      if (!keys || keys.length === 0) continue;
      const og = document.createElement("optgroup");
      og.label = cat;
      for (const k of keys) {
        const opt = document.createElement("option");
        opt.value = k;
        opt.textContent = materialOptionText(k);
        if (k === selected) opt.selected = true;
        og.appendChild(opt);
      }
      sel.appendChild(og);
    }
    wrap.appendChild(sel);

    // Live numeric preview beneath the select.
    const preview = document.createElement("div");
    preview.style.cssText =
      "font-size:10.5px;color:#7f8ea6;margin:3px 2px 0;line-height:1.4;";
    preview.innerHTML = materialPreviewHtml(selected);
    wrap.appendChild(preview);

    sel.addEventListener("change", () => {
      const v = sel.value as MaterialKey;
      preview.innerHTML = materialPreviewHtml(v);
      onChange(v);
    });
    return wrap;
  }

  // ---- build (once) ------------------------------------------------------

  private build(): void {
    this.root = document.createElement("div");
    this.root.style.cssText =
      "background:#0b0e13;color:#e6edf3;font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;" +
      "padding:12px;box-sizing:border-box;max-width:420px;overflow:auto;";

    const title = document.createElement("div");
    title.textContent = "Tower Designer";
    title.style.cssText = "font-size:15px;font-weight:700;margin:0 0 4px;color:#e6edf3;";
    const sub = document.createElement("div");
    sub.textContent = "Design a tower and load it with equipment.";
    sub.style.cssText = "font-size:11px;color:#6b7d91;margin:0 0 12px;";
    this.root.appendChild(title);
    this.root.appendChild(sub);

    // validation box (kept at top, updated on every edit)
    this.validationBox = document.createElement("div");
    this.validationBox.style.cssText =
      "border:1px solid #1c2530;border-radius:8px;padding:8px 10px;margin:0 0 10px;background:#0d1219;";
    this.root.appendChild(this.validationBox);

    // placeholders for the dynamic groups; filled by renderAll()
    this.guysList = document.createElement("div");
    this.loadsList = document.createElement("div");

    this.container.appendChild(this.root);
    this.renderAll();
  }

  /** Rebuild every control group from the current spec. */
  private renderAll(): void {
    // remove all children after the validation box, then re-add groups
    while (this.root.lastChild && this.root.lastChild !== this.validationBox) {
      this.root.removeChild(this.root.lastChild);
    }
    this.root.appendChild(this.renderModeBanner());
    if (!this.drawnMode) {
      this.root.appendChild(this.renderQuickSettings());
      this.root.appendChild(this.renderMaterials());
      this.root.appendChild(this.renderShell());
      this.root.appendChild(this.renderGuys());
    }
    this.root.appendChild(this.renderFoundation());
    this.root.appendChild(this.renderLoads());
    this.runValidation();
  }

  // ---- structure (type + height) ----------------------------------------

  private renderQuickSettings(): HTMLElement {
    const st = (this.spec.stack ??= { ...DEFAULT_STACK });
    const g = this.groupEl("Quick Settings");
    const types: TowerType[] = ["lattice", "guyed", "monopole"];
    g.appendChild(
      this.selectField(
        "Type",
        types.map((t) => ({ value: t, text: t })),
        this.spec.type,
        (v) => {
          this.spec.type = v as TowerType;
          // The pin-jointed solver can't model a single tubular beam, so a monopole
          // is a SLIM, many-sided, densely-braced internal lattice clad in a tapered
          // tube (how every real monopole here is built). Switching TO monopole
          // reconfigures the stack to that tube form + adds the shell; switching away
          // drops the shell.
          const st = this.spec.stack;
          if (this.spec.type === "monopole" && st) {
            st.faces = 8; // rounder polygon → reads as a pole, stiffer in bending
            st.legSection = "legXL"; // pipe legs (yield before buckling)
            st.braceSection = "braceHeavy";
            st.bracing = "X";
            st.panels = Math.max(10, Math.round(this.spec.height / 2.5)); // dense ~2.5 m
            if (st.baseRadius > 1.0) st.baseRadius = Math.max(0.6, this.spec.height * 0.025);
            if (st.topRadius > st.baseRadius) st.topRadius = st.baseRadius * 0.5;
            this.spec.shell = {
              baseDiameter: Math.max(0.4, st.baseRadius * 2),
              topDiameter: Math.max(0.25, st.topRadius * 2),
            };
          } else if (this.spec.type !== "monopole") {
            this.spec.shell = undefined;
          }
          this.renderAll();
          this.emit();
        },
      ),
    );
    g.appendChild(
      this.numberField("Height", this.spec.height, 5, 200, 1, (v) => {
        this.spec.height = v;
        this.emit();
      }, "m"),
    );
    g.appendChild(
      this.selectField(
        "Faces",
        [
          { value: "3", text: "3 — triangular" },
          { value: "4", text: "4 — square" },
        ],
        String(st.faces),
        (v) => {
          st.faces = Number(v);
          this.emit();
        },
      ),
    );
    g.appendChild(
      this.numberField("Panels", st.panels, 1, 60, 1, (v) => {
        st.panels = Math.round(v);
        this.emit();
      }),
    );
    g.appendChild(
      this.numberField("Base radius", st.baseRadius, 0.2, 15, 0.1, (v) => {
        st.baseRadius = v;
        this.emit();
      }, "m"),
    );
    g.appendChild(
      this.numberField("Top radius", st.topRadius, 0.1, 15, 0.1, (v) => {
        st.topRadius = v;
        this.emit();
      }, "m"),
    );
    g.appendChild(
      this.selectField(
        "Bracing",
        [
          { value: "X", text: "X — triangulated (stable)" },
          { value: "none", text: "none — belts only (mechanism!)" },
        ],
        st.bracing ?? "X",
        (v) => {
          st.bracing = v as "X" | "none";
          this.emit();
        },
      ),
    );
    return g;
  }

  // ---- panel stack -------------------------------------------------------

  private renderMaterials(): HTMLElement {
    const st = (this.spec.stack ??= { ...DEFAULT_STACK });
    this.ensureMemberTypes();
    st.legType ??= this.spec.legTypes![0]!.id;
    st.braceType ??= this.spec.braceTypes![0]!.id;
    this.syncStackDefaults();
    const g = this.groupEl("Material Selection");

    g.appendChild(
      this.selectField("Default leg type", this.typeOptions(this.spec.legTypes!), st.legType, (v) => {
        st.legType = v;
        this.syncStackDefaults();
        this.emit();
      }),
    );
    g.appendChild(
      this.selectField("Default brace type", this.typeOptions(this.spec.braceTypes!), st.braceType, (v) => {
        st.braceType = v;
        this.syncStackDefaults();
        this.emit();
      }),
    );
    g.appendChild(this.renderMemberTypeEditor("Leg types", this.spec.legTypes!, "leg", () => {
      if (!this.spec.legTypes!.some((t) => t.id === st.legType)) st.legType = this.spec.legTypes![0]!.id;
      this.syncStackDefaults();
    }));
    g.appendChild(this.renderMemberTypeEditor("Brace types", this.spec.braceTypes!, "brace", () => {
      if (!this.spec.braceTypes!.some((t) => t.id === st.braceType)) st.braceType = this.spec.braceTypes![0]!.id;
      this.syncStackDefaults();
    }));
    g.appendChild(this.renderMemberTypeEditor("Guy types", this.spec.guyTypes!, "guy", () => {
      for (const ring of this.spec.guys ?? []) {
        if (!this.spec.guyTypes!.some((t) => t.id === ring.guyType)) ring.guyType = this.spec.guyTypes![0]!.id;
      }
    }));
    return g;
  }

  // ---- shell (monopole cladding) ----------------------------------------

  private renderShell(): HTMLElement {
    const g = this.groupEl("Tube Shell (monopole cladding)");

    const enabled = !!this.spec.shell;
    const toggleRow = document.createElement("label");
    toggleRow.style.cssText =
      "display:flex;align-items:center;gap:8px;font-size:12px;color:#9fb0c3;margin:0 0 8px;cursor:pointer;";
    const cb = document.createElement("input");
    cb.type = "checkbox";
    cb.checked = enabled;
    cb.style.accentColor = "#38bdf8";
    cb.addEventListener("change", () => {
      if (cb.checked) {
        const stk = (this.spec.stack ??= { ...DEFAULT_STACK });
        this.spec.shell = { baseDiameter: Math.max(0.3, stk.baseRadius * 2), topDiameter: Math.max(0.2, stk.topRadius * 2) };
      } else {
        this.spec.shell = undefined;
      }
      this.renderAll();
      this.emit();
    });
    toggleRow.appendChild(cb);
    toggleRow.appendChild(document.createTextNode("Clad in a tapered steel tube"));
    g.appendChild(toggleRow);

    if (this.spec.shell) {
      const sh = this.spec.shell as TubeShell;
      g.appendChild(
        this.numberField("Base diameter", sh.baseDiameter, 0.2, 30, 0.1, (v) => {
          sh.baseDiameter = v;
          this.emit();
        }, "m"),
      );
      g.appendChild(
        this.numberField("Top diameter", sh.topDiameter, 0.1, 30, 0.1, (v) => {
          sh.topDiameter = v;
          this.emit();
        }, "m"),
      );
    }
    return g;
  }

  // ---- guys --------------------------------------------------------------

  private renderGuys(): HTMLElement {
    const g = this.groupEl("Guy Rings");
    this.guysList = document.createElement("div");
    g.appendChild(this.guysList);

    const add = this.button("+ Add guy ring", "primary");
    add.addEventListener("click", () => {
      this.ensureMemberTypes();
      const ring: GuyRing = {
        atHeightFrac: 0.8,
        anchorRadius: Math.max(1, Math.round(this.spec.height * 0.7)),
        section: "guy38",
        material: "guyStrand",
        guyType: this.spec.guyTypes?.[0]?.id,
        pretensionStrain: 0.0015,
      };
      (this.spec.guys ??= []).push(ring);
      this.renderGuysList();
      this.emit();
    });
    g.appendChild(add);

    this.renderGuysList();
    return g;
  }

  private renderGuysList(): void {
    const list = this.guysList;
    list.innerHTML = "";
    this.ensureMemberTypes();
    const guys = this.spec.guys ?? [];
    if (guys.length === 0) {
      const empty = document.createElement("div");
      empty.textContent = "No guys. Add rings for a guyed mast.";
      empty.style.cssText = "font-size:11px;color:#6b7d91;margin:0 0 8px;";
      list.appendChild(empty);
      return;
    }
    guys.forEach((ring, i) => {
      const card = document.createElement("div");
      card.style.cssText =
        "border:1px solid #223142;border-radius:6px;padding:8px;margin:0 0 8px;background:#0f1620;";

      const head = document.createElement("div");
      head.style.cssText = "display:flex;justify-content:space-between;align-items:center;margin:0 0 6px;";
      const t = document.createElement("span");
      t.textContent = `Guy ring ${i + 1}`;
      t.style.cssText = "font-size:12px;font-weight:600;color:#cfe8ff;";
      const rm = this.button("Remove", "danger");
      rm.addEventListener("click", () => {
        guys.splice(i, 1);
        if (guys.length === 0) this.spec.guys = [];
        this.renderGuysList();
        this.emit();
      });
      head.appendChild(t);
      head.appendChild(rm);
      card.appendChild(head);

      card.appendChild(
        this.numberField("Height fraction", ring.atHeightFrac, 0.05, 1, 0.05, (v) => {
          ring.atHeightFrac = v;
          this.emit();
        }),
      );
      card.appendChild(
        this.numberField("Anchor radius", ring.anchorRadius, 1, 200, 1, (v) => {
          ring.anchorRadius = v;
          this.emit();
        }, "m"),
      );
      card.appendChild(
        this.selectField("Guy type", this.typeOptions(this.spec.guyTypes!), ring.guyType ?? this.spec.guyTypes![0]!.id, (v) => {
          ring.guyType = v;
          const type = this.spec.guyTypes!.find((t) => t.id === v);
          if (type) {
            ring.section = type.section;
            ring.material = type.material;
          }
          this.emit();
        }),
      );
      card.appendChild(
        this.selectField("Guy section", this.sectionOptions(), sectionLabel(ring.section), (v) => {
          ring.section = v as SectionKey;
          ring.guyType = undefined;
          this.emit();
        }),
      );
      card.appendChild(
        this.materialSelectField("Guy material", resolveMaterialKey(ring.material ?? "guyStrand"), (v) => {
          ring.material = v;
          ring.guyType = undefined;
          this.emit();
        }),
      );
      card.appendChild(
        this.numberField("Pretension strain", ring.pretensionStrain, 0, 0.02, 0.0005, (v) => {
          ring.pretensionStrain = v;
          this.emit();
        }),
      );
      list.appendChild(card);
    });
  }

  // ---- foundation & ground (soil / concrete / footing) -------------------

  private renderFoundation(): HTMLElement {
    const g = this.groupEl("Foundation & ground");
    const f = (this.spec.foundation ??= {});

    // Soil class — drives bearing / uplift / sliding (physics/foundation.ts).
    g.appendChild(
      this.selectField(
        "Soil",
        (Object.keys(SOIL_PRESETS) as SoilType[]).map((k) => ({ value: k, text: SOIL_PRESETS[k].name })),
        f.soil ?? "stiff-clay",
        (v) => {
          f.soil = v as SoilType;
          this.emit();
        },
      ),
    );

    // Base footing.
    const base = (f.base ??= { type: "pad", width: 2.0, diameter: 0.6, depth: 1.5, concrete: "c20" });
    g.appendChild(
      this.selectField(
        "Footing",
        [
          { value: "pad", text: "Spread pad" },
          { value: "pier", text: "Drilled pier" },
        ],
        base.type === "pier" ? "pier" : "pad",
        (v) => {
          base.type = v as FoundationType;
          this.renderAll(); // swap the pad-width / pier-diameter field label
          this.emit();
        },
      ),
    );
    const isPier = base.type === "pier";
    g.appendChild(
      this.numberField(
        isPier ? "Pier diameter" : "Pad width",
        isPier ? base.diameter : base.width,
        0.3,
        8,
        0.1,
        (val) => {
          if (isPier) base.diameter = val;
          else base.width = val;
          this.emit();
        },
        "m",
      ),
    );
    g.appendChild(
      this.numberField("Depth", base.depth, 0.5, 8, 0.1, (v) => {
        base.depth = v;
        this.emit();
      }, "m"),
    );
    g.appendChild(
      this.selectField(
        "Concrete",
        (Object.keys(CONCRETE_GRADES) as ConcreteGradeKey[]).map((k) => ({ value: k, text: CONCRETE_GRADES[k].name })),
        base.concrete,
        (v) => {
          base.concrete = v as ConcreteGradeKey;
          this.emit();
        },
      ),
    );

    // Guy anchors (guyed masts only).
    if (this.spec.type === "guyed") {
      const anc = (f.anchor ??= { type: "deadman", width: 1.5, diameter: 0.6, depth: 2.0, concrete: "c20" });
      g.appendChild(
        this.numberField("Guy anchor block", anc.width, 0.5, 6, 0.1, (v) => {
          anc.width = v;
          this.emit();
        }, "m"),
      );
      g.appendChild(
        this.numberField("Anchor depth", anc.depth, 0.5, 8, 0.1, (v) => {
          anc.depth = v;
          this.emit();
        }, "m"),
      );
    }
    return g;
  }

  // ---- equipment loadout -------------------------------------------------

  private renderLoads(): HTMLElement {
    const g = this.groupEl("Equipment Loadout");

    // add-row: pick catalog item + press add
    const addRow = document.createElement("div");
    addRow.style.cssText = "display:flex;gap:6px;margin:0 0 8px;";
    const pick = document.createElement("select");
    this.styleInput(pick);
    pick.style.flex = "1";
    for (const k of CATALOG_KEYS) {
      const opt = document.createElement("option");
      opt.value = k;
      opt.textContent = CATALOG[k].name;
      pick.appendChild(opt);
    }
    const add = this.button("+ Add", "primary");
    add.addEventListener("click", () => {
      const key = pick.value as CatalogKey;
      const app = CATALOG[key];
      let height = Math.round(this.spec.height * 0.9);
      let azimuth = 0;
      if (app.kind === "panel") {
        // Auto-arrange panels in rings around the tower. Within a ring the azimuth
        // densifies — offsets +90,+90,+45,+90,+90 → [0,90,180,225,315,45]° — and
        // every 6th panel drops to a lower ring, so adding panels stacks realistic
        // sector rings down the mast instead of piling them at one spot.
        const n = (this.spec.loads ?? []).filter((l) => l.appurtenance.kind === "panel").length;
        const AZ_DEG = [0, 90, 180, 225, 315, 45];
        const ring = Math.floor(n / AZ_DEG.length);
        const pos = n % AZ_DEG.length;
        azimuth = ((AZ_DEG[pos] ?? 0) * Math.PI) / 180;
        const drop = Math.max(2, this.spec.height * 0.06); // ring spacing (m)
        height = Math.max(3, Math.round(this.spec.height * 0.9 - ring * drop));
      }
      const load: MountedLoad = { appurtenance: app, height, azimuth };
      (this.spec.loads ??= []).push(load);
      this.renderLoadsList();
      this.emit();
    });
    addRow.appendChild(pick);
    addRow.appendChild(add);
    g.appendChild(addRow);

    this.loadsList = document.createElement("div");
    g.appendChild(this.loadsList);
    this.renderLoadsList();
    return g;
  }

  private renderLoadsList(): void {
    const list = this.loadsList;
    list.innerHTML = "";
    const loads = this.spec.loads ?? [];
    if (loads.length === 0) {
      const empty = document.createElement("div");
      empty.textContent = "No equipment mounted.";
      empty.style.cssText = "font-size:11px;color:#6b7d91;";
      list.appendChild(empty);
      return;
    }
    loads.forEach((load, i) => {
      const card = document.createElement("div");
      card.style.cssText =
        "border:1px solid #223142;border-radius:6px;padding:8px;margin:0 0 8px;background:#0f1620;";

      const head = document.createElement("div");
      head.style.cssText = "display:flex;justify-content:space-between;align-items:center;margin:0 0 6px;gap:8px;";
      const t = document.createElement("span");
      const a = load.appurtenance;
      t.textContent = `${a.name}`;
      t.style.cssText = "font-size:12px;font-weight:600;color:#cfe8ff;";
      const rm = this.button("Remove", "danger");
      rm.addEventListener("click", () => {
        loads.splice(i, 1);
        if (loads.length === 0) this.spec.loads = [];
        this.renderLoadsList();
        this.emit();
      });
      head.appendChild(t);
      head.appendChild(rm);
      card.appendChild(head);

      const meta = document.createElement("div");
      meta.style.cssText = "font-size:10px;color:#6b7d91;margin:0 0 6px;";
      const per = a.kind === "coax" ? "/m" : "";
      meta.textContent = `EPA ${a.epa} m²${per} · ${a.weight} kg${per}`;
      card.appendChild(meta);

      card.appendChild(
        this.numberField("Height", load.height, 0, Math.max(5, this.spec.height), 0.5, (v) => {
          load.height = v;
          this.emit();
        }, "m"),
      );
      // azimuth in degrees for the UI, stored as radians on the load
      card.appendChild(
        this.numberField(
          "Azimuth",
          Math.round(((load.azimuth ?? 0) * 180) / Math.PI),
          0,
          360,
          5,
          (v) => {
            load.azimuth = (v * Math.PI) / 180;
            this.emit();
          },
          "°",
        ),
      );
      if (a.kind === "coax") {
        card.appendChild(
          this.numberField("Run length", load.runLength ?? load.height, 1, Math.max(5, this.spec.height), 0.5, (v) => {
            load.runLength = v;
            this.emit();
          }, "m"),
        );
      }
      list.appendChild(card);
    });
  }
}
